import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import {
  iosNativeCenterHeaderLeft,
  iosNativeContent,
  iosNativePageRoot,
  iosNativeScreenSubtitle,
  iosNativeScreenTitle,
} from '../styles/iosNativePageCss';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Check, Sparkles, Crown, Star, Zap, Info } from 'lucide-react';
import { useAWSAuth } from '../contexts/AWSAuthContext';
import { createCheckoutSession } from '../services/stripeService';
import { useToast } from '../components/ui/Toast';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { TrialExpiredBanner } from '../components/TrialExpiredBanner';
import { Browser } from '@capacitor/browser';
import { isNative } from '../utils/platformUtils';

const PageContainer = styled.div`
  min-height: calc(100vh - 72px);
  background: linear-gradient(180deg, ${theme.colors.background.secondary} 0%, ${theme.colors.background.primary} 100%);
  padding: 2rem 1rem 4rem;

  ${iosNativePageRoot}
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;

  ${iosNativeContent}
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  animation: slideUp 0.5s ease-out;

  ${iosNativeCenterHeaderLeft}
`;

const Title = styled.h1`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize['4xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;

  ${iosNativeScreenTitle}
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  color: ${theme.colors.text.secondary};
  max-width: 600px;
  margin: 0 auto 2rem;

  body.native-app & {
    margin-left: 0;
    margin-right: 0;
  }

  ${iosNativeScreenSubtitle}
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const ToggleLabel = styled.span<{ $active: boolean }>`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${props => props.$active ? theme.typography.fontWeight.semibold : theme.typography.fontWeight.normal};
  color: ${props => props.$active ? theme.colors.text.primary : theme.colors.text.tertiary};
  transition: all 0.2s ease;
`;

const Toggle = styled.button<{ $isYearly: boolean }>`
  position: relative;
  width: 56px;
  height: 32px;
  background: ${theme.colors.primary[500]};
  border-radius: ${theme.borderRadius.full};
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;

  &::after {
    content: '';
    position: absolute;
    top: 4px;
    left: ${props => props.$isYearly ? '28px' : '4px'};
    width: 24px;
    height: 24px;
    background: white;
    border-radius: ${theme.borderRadius.full};
    transition: all 0.3s ease;
  }
`;

const SaveNotice = styled.div`
  text-align: center;
  color: ${theme.colors.mint[600]};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  margin-bottom: 2rem;
`;

const PricingGrid = styled.div`
  display: grid;
  gap: 2rem;
  margin-bottom: 2rem;
  grid-template-columns: 1fr;

  @media (min-width: ${theme.breakpoints.sm}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.75rem;
  }

  @media (min-width: ${theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }

  @media (min-width: ${theme.breakpoints.lg}) {
    grid-template-columns: repeat(3, 1fr);
    gap: 2rem;
  }

  @media (min-width: 1280px) {
    gap: 2.5rem;
  }
`;

const PricingCard = styled(Card)<{ popular?: boolean; elite?: boolean; delay?: string; $isCurrentPlan?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.6s ease-out;
  animation-delay: ${(props) => props.delay || '0s'};
  border: ${props => {
    if (props.$isCurrentPlan) return `2px solid ${theme.colors.mint[500]}`;
    if (props.popular) return `3px solid ${theme.colors.primary[400]}`;
    if (props.elite) return `3px solid ${theme.colors.status.warning}`;
    return 'none';
  }};
  transform: ${props => props.popular ? 'scale(1.05)' : 'scale(1)'};
  background: ${props => props.elite ? `linear-gradient(135deg, ${theme.colors.background.primary} 0%, ${theme.colors.background.warm} 100%)` : theme.colors.background.primary};
  margin: 0;

  @media (min-width: ${theme.breakpoints.md}) {
    transform: ${props => props.popular ? 'scale(1.08)' : 'scale(1)'};
  }
`;

const PopularBadge = styled(Badge)`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
`;

const CurrentPlanBadge = styled(Badge)`
  position: absolute;
  top: -12px;
  right: 1rem;
  background: ${theme.colors.mint[500]};
  color: white;
`;

const ComingSoonButton = styled(Button)`
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 2px solid #f59e0b;
  color: #92400e;
  font-weight: ${theme.typography.fontWeight.bold};
  cursor: not-allowed;
  opacity: 0.9;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    transition: left 0.5s;
  }

  &:hover {
    background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
    border-color: #f59e0b;
    color: #92400e;
    transform: none;
    box-shadow: 0 2px 8px rgba(245, 158, 11, 0.2);
    
    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: none;
  }
`;

const EliteBadge = styled(Badge)`
  position: absolute;
  top: calc(-12px * 1.21275);
  left: 50%;
  transform: translateX(-50%);
  background: ${theme.colors.status.warning};
  color: white;
`;

const PlanIcon = styled.div<{ $color: string }>`
  width: 56px;
  height: 56px;
  margin: 0 auto 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$color}20;
  border-radius: ${theme.borderRadius.xl};
  color: ${props => props.$color};

  svg {
    width: 28px;
    height: 28px;
  }
`;

const PlanName = styled.h3`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
  text-align: center;
  margin-bottom: 0.5rem;
`;

const PriceContainer = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
`;

const Price = styled.div`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize['4xl']};
  font-weight: ${theme.typography.fontWeight.extrabold};
  color: ${theme.colors.text.primary};
  line-height: 1;

  span {
    font-size: ${theme.typography.fontSize.xl};
    color: ${theme.colors.text.tertiary};
  }
`;

const PriceNote = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.tertiary};
  margin-top: 0.5rem;
`;

const FeaturesList = styled.ul<{ $planId?: string }>`
  list-style: none;
  padding: 0;
  margin: 0 0 2rem;
  display: flex;
  flex-direction: column;
  gap: ${props => {
    if (props.$planId === 'plus') return '2.008rem'; /* ~168% more spacing for Plus plan */
    if (props.$planId === 'pro') return '1.08rem'; /* 44% more spacing for Pro plan (20% + 20%) */
    return '0.75rem';
  }};
  flex: 1;
`;

const Feature = styled.li<{ $disabled?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  font-size: ${theme.typography.fontSize.sm};
  color: ${props => props.$disabled ? theme.colors.text.tertiary : theme.colors.text.secondary};
  opacity: ${props => props.$disabled ? 0.5 : 1};

  svg {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    color: ${theme.colors.mint[500]};
    margin-top: 2px;
  }
`;


const UpgradeBanner = styled.div`
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 2px solid #f59e0b;
  border-radius: ${theme.borderRadius.lg};
  padding: 1.25rem 1.5rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  animation: slideUp 0.5s ease-out;
`;

const UpgradeBannerContent = styled.div`
  flex: 1;
`;

const UpgradeBannerTitle = styled.h3`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.bold};
  color: #92400e;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const UpgradeBannerText = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: #78350f;
  margin: 0;
  line-height: 1.6;
`;

const FreeTrialNotice = styled.div`
  background: linear-gradient(135deg, ${theme.colors.mint[50]} 0%, ${theme.colors.primary[50]} 100%);
  border: 2px solid ${theme.colors.mint[300]};
  border-radius: ${theme.borderRadius.xl};
  padding: 2rem;
  margin-top: 3rem;
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
  animation: slideUp 0.6s ease-out;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);

  @media (max-width: ${theme.breakpoints.md}) {
    flex-direction: column;
    padding: 1.5rem;
    gap: 1rem;
  }
`;

const FreeTrialIcon = styled.div`
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, ${theme.colors.mint[400]} 0%, ${theme.colors.primary[500]} 100%);
  border-radius: ${theme.borderRadius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const FreeTrialContent = styled.div`
  flex: 1;
`;

const FreeTrialTitle = styled.h3`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
  margin: 0 0 0.75rem 0;
`;

const FreeTrialText = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.text.secondary};
  line-height: 1.7;
  margin: 0 0 1rem 0;
`;

const FreeTrialFeatures = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const FreeTrialFeature = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const PricingPage: React.FC = () => {
  const [isYearly, setIsYearly] = useState(false);
  const [loading, setLoading] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userProfile } = useAWSAuth();
  const { showToast } = useToast();
  const subscriptionState = useSubscriptionStatus();
  const currentTier = userProfile?.subscriptionTier || 'plus';
  const upgradeParam = searchParams.get('upgrade');
  const isEliteUpgrade = upgradeParam === 'elite';

  const handleSelectPlan = async (planId: 'plus' | 'pro' | 'elite') => {
    if (!userProfile?.uid) {
      showToast('Please sign in to subscribe', 'error');
      navigate('/signin');
      return;
    }

    setLoading(planId);
    try {
      const { url } = await createCheckoutSession(planId, userProfile.uid);
      
      if (!url) {
        throw new Error('No checkout URL received');
      }

      // Redirect to Stripe Checkout
      if (isNative()) {
        await Browser.open({ url });
      } else {
        window.location.href = url;
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      
      // Show user-friendly error message
      const errorMessage = error.message || 'Something went wrong starting checkout. Please try again.';
      showToast(errorMessage, 'error');
      setLoading(null);
    }
  };

  const plans = [
    {
      id: 'plus',
      name: 'GlowLens Plus',
      icon: Star,
      iconColor: theme.colors.primary[600],
      monthlyPrice: 4.99,
      yearlyPrice: 39.99,
      features: [
        { text: 'Unlimited skin scans', included: true },
        { text: 'Smart acne analysis + basic guidance', included: true },
        { text: 'Acne + oily skin focus only', included: true },
        { text: 'Basic ingredient safety checks', included: true },
        { text: 'DIY mask library', included: true },
        { text: 'Glow progress tracking', included: true },
        { text: 'Unlimited food scanner at home', included: true },
        { text: 'Basic food guidance (simple tips)', included: true },
        { text: 'Prescription-aware guidance & warnings', included: true },
        { text: 'Priority support', included: true },
      ],
      cta: 'Start Free Trial',
      popular: false,
      tagline: 'Starter Tier - Worth the price',
    },
    {
      id: 'pro',
      name: 'GlowLens Pro',
      icon: Crown,
      iconColor: theme.colors.mint[600],
      monthlyPrice: 14.99,
      yearlyPrice: 99.99,
      features: [
        { text: 'Everything in Plus', included: true },
        { text: 'Advanced AI skin analysis with expanded skin types', included: true },
        { text: 'Expanded skin conditions (dryness, sensitivity included)', included: true },
        { text: 'Personalized diet plan based on scans', included: true },
        { text: 'Full food library + "Show more foods"', included: true },
        { text: 'Cuisine filters', included: true },
        { text: 'Restaurant food scanner', included: true },
        { text: 'Priority AI support + product detection', included: true },
        { text: 'Weekly improvement insights', included: true },
        { text: 'Full habit tracking (sleep/water/stress)', included: true },
        { text: 'Unlimited DIY mask recommendations', included: true },
        { text: 'Prescription-aware guidance & warnings', included: true },
      ],
      cta: 'Start Free Trial',
      popular: false,
      tagline: '',
    },
    {
      id: 'elite',
      name: 'GlowLens Elite',
      icon: Zap,
      iconColor: theme.colors.status.warning,
      monthlyPrice: 69.99,
      yearlyPrice: 699.99,
      features: [
        { text: 'Everything in Pro', included: true },
        { text: 'Derm-grade skin insights + optional expert review', included: true },
        { text: 'Covers all major skin issues: acne, oil, dryness, sensitivity, eczema, rosacea, hyperpigmentation, aging', included: true },
        { text: '24/7 VIP AI skin coach', included: true },
        { text: 'Tele-dermatology consults (1x/month)', included: true },
        { text: 'Prescription-aware guidance & warnings', included: true },
        { text: 'Routine builder with direct expert support', included: true },
        { text: 'Exportable medical-style progress reports', included: true },
        { text: 'Personalized goals + accountability coaching', included: true },
      ],
      cta: 'Coming Soon',
      popular: false,
      elite: true,
      tagline: 'Premium Coaching - Justified value',
      comingSoon: true,
    },
  ];

  return (
    <PageContainer>
      <Container>
        <Header>
          <Title>Choose Your Glow Plan ✨</Title>
          <Subtitle>
            Experience all premium features with a 7-day free trial. Choose your plan after exploring what works best for you. Cancel anytime, no questions asked. 💚
          </Subtitle>

          <ToggleContainer>
            <ToggleLabel $active={!isYearly}>Monthly</ToggleLabel>
            <Toggle $isYearly={isYearly} onClick={() => setIsYearly(!isYearly)} />
            <ToggleLabel $active={isYearly}>Yearly</ToggleLabel>
          </ToggleContainer>

          {isYearly && (
            <SaveNotice>
              💰 Save 33% — pay once, glow all year!
            </SaveNotice>
          )}
        </Header>

        {/* Trial Expired Banner */}
        {subscriptionState.isTrialEnded && (
          <TrialExpiredBanner onChoosePlan={() => {}} />
        )}

        {/* Upgrade Banner - Shown when redirected from VIP Chat */}
        {isEliteUpgrade && (
          <UpgradeBanner>
            <Info size={24} color="#92400e" style={{ flexShrink: 0, marginTop: '2px' }} />
            <UpgradeBannerContent>
              <UpgradeBannerTitle>
                VIP Chat is only available on GlowLens Elite
              </UpgradeBannerTitle>
              <UpgradeBannerText>
                Upgrade to unlock 24/7 AI skin coaching + dermatologist-style guidance. Get instant answers to all your skincare questions, personalized routine recommendations, and priority support.
              </UpgradeBannerText>
            </UpgradeBannerContent>
          </UpgradeBanner>
        )}

        <PricingGrid>
          {plans.map((plan, index) => {
            const Icon = plan.icon;
            const displayPrice = isYearly ? plan.yearlyPrice : plan.monthlyPrice;
            const priceText = displayPrice === 0 ? 'Free' : `$${displayPrice}`;
            const periodText = displayPrice === 0 ? 'forever' : isYearly ? '/year' : '/month';
            const isCurrentPlan = currentTier === plan.id;
            const showManageButton = isCurrentPlan;

            return (
              <PricingCard
                key={plan.name}
                padding="lg"
                popular={plan.popular}
                elite={plan.elite}
                delay={`${index * 0.1}s`}
                $isCurrentPlan={isCurrentPlan}
              >
                {plan.popular && (
                  <PopularBadge variant="popular">
                    ⭐️ {plan.tagline}
                  </PopularBadge>
                )}
                {plan.elite && (
                  <EliteBadge>
                    🚀 {plan.tagline}
                  </EliteBadge>
                )}
                
                <PlanIcon $color={plan.iconColor}>
                  <Icon />
                </PlanIcon>

                <PlanName>{plan.name}</PlanName>

                <PriceContainer>
                  <Price>
                    {priceText}
                    <span>{periodText}</span>
                  </Price>
                  {isYearly && displayPrice > 0 && (
                    <PriceNote>
                      ${(displayPrice / 12).toFixed(2)}/month
                      {plan.id === 'elite' && ' (2 months free)'}
                    </PriceNote>
                  )}
                </PriceContainer>

                <FeaturesList $planId={plan.id}>
                  {plan.features.map((feature, i) => (
                    <Feature key={i} $disabled={!feature.included}>
                      {feature.included ? <Check /> : <span style={{ width: '20px' }}>—</span>}
                      {feature.text}
                    </Feature>
                  ))}
                </FeaturesList>

                {plan.comingSoon ? (
                  <ComingSoonButton
                    fullWidth
                    size="lg"
                    disabled
                  >
                    ✨ Coming Soon
                  </ComingSoonButton>
                ) : (
                  <Button
                    fullWidth
                    size="lg"
                    variant={plan.popular || plan.elite ? 'primary' : 'secondary'}
                    onClick={() => {
                      if (showManageButton) {
                        navigate('/profile');
                      } else {
                        handleSelectPlan(plan.id as 'plus' | 'pro' | 'elite');
                      }
                    }}
                    disabled={loading === plan.id}
                  >
                    {loading === plan.id 
                      ? 'Loading...' 
                      : showManageButton 
                        ? 'Manage subscription' 
                        : plan.cta}
                  </Button>
                )}
              </PricingCard>
            );
          })}
        </PricingGrid>

        <FreeTrialNotice>
          <FreeTrialIcon>
            <Sparkles size={24} />
          </FreeTrialIcon>
          <FreeTrialContent>
            <FreeTrialTitle>7-Day Free Trial for All Plans</FreeTrialTitle>
            <FreeTrialText>
              As a new user, you'll receive a complimentary 7-day free trial to explore all premium features across Plus, Pro, and Elite plans. During your trial period, you can switch between plans at any time to discover which tier best suits your skincare journey. After your trial ends, simply choose the plan that works best for you—no commitment required until you decide.
            </FreeTrialText>
            <FreeTrialFeatures>
              <FreeTrialFeature>✨ Full access to all premium features</FreeTrialFeature>
              <FreeTrialFeature>🔄 Switch between plans anytime during trial</FreeTrialFeature>
              <FreeTrialFeature>💚 Cancel anytime—no questions asked</FreeTrialFeature>
            </FreeTrialFeatures>
          </FreeTrialContent>
        </FreeTrialNotice>
      </Container>
    </PageContainer>
  );
};

export default PricingPage;
